import React, { useRef, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Tab,
  Tabs,
  Modal,
  Dropdown
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminLogsAPI, transportOrdersAPI, driversVehiclesAPI, chatTransportOrdersAPI } from '../../api/apiClient';
import { useAuth } from "../../context/AuthContext";
import "../../assets/css/OrderDetails.css";
import Swal from 'sweetalert2';
import { 
  FaFacebookMessenger, 
  FaEye, 
  FaEyeSlash, 
  FaEuroSign, 
  FaCheck, 
  FaTimes, 
  FaBolt, 
  FaTrashAlt, 
  FaArrowLeft, 
  FaMoneyBillWave, 
  FaCog, 
  FaFileInvoice, 
  FaTruck, 
  FaSearch, 
  FaInfoCircle,
  FaMapMarkerAlt,
  FaCalendar,
  FaTruckLoading,
  FaTruckMoving,
  FaBox,
  FaUser,
  FaPhone,
  FaRoad,
  FaWeightHanging,
  FaClock,
  FaHistory,
  FaCar,
  FaList,
  FaTypo3,
  FaSync,
  FaLocationArrow,
  FaPaperPlane,
  FaImage,
  FaFile,
  FaLockOpen,
  FaLock,
  FaUnlock
} from "react-icons/fa";
import { FaBoxesStacked, FaMessage } from "react-icons/fa6";
import Pusher from 'pusher-js';
import '../../assets/css/OrderChat.css';
import { formatNumber } from "../../utils/helpers";

export default function TransportOrderDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const highlightId = location.state?.highlightId;
  const { user } = useAuth(); // الحصول على معلومات المستخدم الحالي

  // States for chat
  const [orderChats, setOrderChats] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [pusher, setPusher] = useState(null);
  const [channel, setChannel] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [hasMarkedAsRead, setHasMarkedAsRead] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transportOrder, setTransportOrder] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [updating, setUpdating] = useState(false);

  const [isChatEnabled, setIsChatEnabled] = useState(true);
  const [isTogglingChat, setIsTogglingChat] = useState(false);

  // Initialize Pusher for real-time chat
  const initializePusher = () => {
    if (!transportOrder) return;

    const pusherInstance = new Pusher("1a6dafa4504c853b7f61", {
      cluster: "mt1",
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
    });

    // Channel name based on transport and order IDs
    const channelName = `transport-order-${transportOrder.transport_id}-${transportOrder.id}`;
    const channelInstance = pusherInstance.subscribe(channelName);

    channelInstance.bind('new-message', (data) => {
      console.log('📨 New message received via Pusher:', data);
      setOrderChats(prev => {
        // التحقق أن prev مصفوفة
        const currentMessages = Array.isArray(prev) ? prev : [];
        
        // Avoid duplicates
        if (currentMessages.some(msg => msg.id === data.message.id)) return currentMessages;
        
        // زيادة العداد إذا كانت الرسالة جديدة وغير مقروءة
        if (!data.message.is_read && data.message.sender?.id !== user?.id) {
          setUnreadCount(prevCount => prevCount + 1);
        }
        
        return [...currentMessages, data.message];
      });
      scrollToBottom();
    });

    setPusher(pusherInstance);
    setChannel(channelInstance);
  };

  // Cleanup Pusher on unmount
  useEffect(() => {
    return () => {
      if (channel) {
        channel.unbind_all();
        channel.unsubscribe();
      }
      if (pusher) {
        pusher.disconnect();
      }
    };
  }, [channel, pusher]);

  useEffect(() => {
    fetchTransportOrderDetails();
  }, [id]);

  useEffect(() => {
    if (transportOrder) {
      fetchOrderChats();
      initializePusher();
    }
  }, [transportOrder]);

  useEffect(() => {
    scrollToBottom();
  }, [orderChats]);

  const fetchTransportOrderDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await transportOrdersAPI.show(id);
      
      if (response.success) {
        const data = response.data.data || response.data;
        setTransportOrder(data);
        setIsChatEnabled(data.is_chat_enabled);

      } else {
        setError(response.message || t('failed_to_fetch_transport_details'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transport_details'));
      console.error('Error fetching transport details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderChats = async () => {
    if (!transportOrder) return;

    try {
      setChatLoading(true);
      const response = await chatTransportOrdersAPI.getMessages(
        transportOrder.transport.id, 
        transportOrder.id
      );

      setIsChatEnabled(response.data.data.statistics.is_chat_enabled);

      console.log('Chat API response:', response);
      
      if (response.success) {
        // التحقق من هيكل الاستجابة واستخراج الرسائل
        let messages = [];
        
        if (Array.isArray(response.data)) {
          messages = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          messages = response.data.data;
        } else if (response.data && Array.isArray(response.data.messages)) {
          messages = response.data.messages;
        } else if (response.data?.data && Array.isArray(response.data.data.messages)) {
          messages = response.data.data.messages;
        } else {
          // إذا كان الرد ليس مصفوفة، نقوم بتحويله إلى مصفوفة فارغة
          console.warn('Unexpected chat response format:', response.data);
          messages = [];
        }
        
        setOrderChats(messages);
        
        // حساب عدد الرسائل غير المقروءة
        const unread = messages.filter(msg => !msg.is_read && msg.sender?.id !== user?.id).length;
        setUnreadCount(unread);
        
        // تعليم الرسائل كمقروءة إذا كان هناك رسائل غير مقروءة
        if (unread > 0) {
          setTimeout(() => {
            markMessagesAsRead();
          }, 500);
        }
      } else {
        console.error('Failed to fetch chats:', response.message);
        setOrderChats([]); // تعيين مصفوفة فارغة في حالة الفشل
      }
    } catch (err) {
      console.error('Error fetching order chats:', err);
      setOrderChats([]); // تعيين مصفوفة فارغة في حالة الخطأ
    } finally {
      setChatLoading(false);
    }
  };

  // دالة لتعليم الرسائل كمقروءة
  const markMessagesAsRead = async () => {
    if (!transportOrder || hasMarkedAsRead || unreadCount === 0) return;

    try {
      const transportId = transportOrder.transport?.id || transportOrder.transport_id;
      const orderId = transportOrder.id;

      if (!transportId || !orderId) return;

      console.log("📖 Marking messages as read for order:", orderId);

      const response = await chatTransportOrdersAPI.markAsRead(transportId, orderId);

      if (response.success) {
        console.log("✅ Messages marked as read successfully");
        setUnreadCount(0);
        setHasMarkedAsRead(true);
        
        // تحديث الرسائل المحلية لتظهر كمقروءة
        setOrderChats(prev => {
          const currentMessages = Array.isArray(prev) ? prev : [];
          return currentMessages.map(msg => ({
            ...msg,
            is_read: true
          }));
        });
      } else {
        console.error("❌ Failed to mark messages as read:", response.message);
      }
    } catch (err) {
      console.error("❌ Error marking messages as read:", err);
    }
  };

  // إرسال رسالة جديدة
  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !transportOrder) return;

    try {
      setSending(true);

      const transportId = transportOrder.transport?.id || transportOrder.transport_id;
      const orderId = transportOrder.id;

      if (!transportId || !orderId) {
        throw new Error("Missing transport or order ID");
      }

      console.log("🚀 Sending message:", newMessage.trim());

      const formData = new FormData();
      formData.append('message', newMessage.trim());
      
      if (selectedFile) {
        formData.append('attachments[]', selectedFile);
      }

      const response = await chatTransportOrdersAPI.sendMessage(transportId, orderId, formData);

      console.log("📤 Send message response:", response);

      if (response.success) {
        setNewMessage("");
        setSelectedFile(null);
        setFilePreview(null);
        console.log("✅ Message sent successfully");
        
        // تحديث الرسائل
        fetchOrderChats();
      } else {
        throw new Error(response.message || "Failed to send message");
      }
    } catch (err) {
      console.error("❌ Error sending message:", err);
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: err.message || t('failed_to_send_message'),
        timer: 3000,
      });
    } finally {
      setSending(false);
    }
  };

  // اختيار ملف
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // التحقق من حجم الملف (10MB كحد أقصى)
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('file_size_exceeded'),
        timer: 3000,
      });
      return;
    }

    setSelectedFile(file);

    // إنشاء معاينة للصورة
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleRefreshChat = () => {
    fetchOrderChats();
  };

  // Format time for messages
  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for messages
  const formatMessageDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('yesterday');
    } else {
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups = {};
    
    // التحقق أن orderChats مصفوفة قبل استخدام forEach
    if (!Array.isArray(orderChats)) {
      console.warn('orderChats is not an array:', orderChats);
      return groups;
    }
    
    orderChats.forEach(message => {
      const date = formatMessageDate(message.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    
    return groups;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // التحقق إذا كان المستخدم الحالي هو مرسل الرسالة
  const isCurrentUser = (message) => {
    return user && message.sender?.id === user.id;
  };

  // عرض المرفقات
  const renderAttachment = (attachment) => {
    if (!attachment) return null;
    
    if (attachment.is_image) {
      return (
        <div className="attachment-container mb-2">
          <img 
            src={attachment.file_url} 
            alt={attachment.filename || 'Attachment'}
            className="img-fluid rounded attachment-image"
            style={{ maxHeight: '200px', cursor: 'pointer' }}
            onClick={() => window.open(attachment.file_url, '_blank')}
          />
          <div className="attachment-actions mt-1">
            <small className="text-muted me-2">{attachment.file_size_formatted || ''}</small>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => window.open(attachment.file_url, '_blank')}
            >
              <FaEye className="me-1" /> {t("view")}
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="attachment-container mb-2">
          <div className="file-attachment p-2 border rounded bg-light d-flex align-items-center">
            <FaFile className="text-primary me-2" size={20} />
            <div className="flex-grow-1">
              <div className="fw-bold">{attachment.filename || 'File'}</div>
              <small className="text-muted">{attachment.file_size_formatted || ''}</small>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderOrderChat = () => {
    const groupedMessages = groupMessagesByDate();
    const messageGroups = Object.entries(groupedMessages);

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5 className="mb-0 fw-bold">
              <FaMessage className="me-2 text-primary" />
              {t("order_chat")}
              {transportOrder.customer_id === null && (
                <>
                  {unreadCount > 0 && (
                    <Badge bg="danger" className="ms-2">
                      {unreadCount}
                    </Badge>
                  )}
                </>
              )}
            </h5>
          </div>
          <div className="d-flex align-items-center gap-2">
              <Button 
                variant={isChatEnabled ? "outline-success" : "outline-secondary"} 
                size="sm"
                onClick={toggleChatStatus}
                disabled={isTogglingChat}
                title={isChatEnabled ? t('disable_chat') : t('enable_chat')}
              >
                {isTogglingChat ? (
                  <Spinner animation="border" size="sm" />
                ) : isChatEnabled ? (
                  <FaLockOpen className="me-1" />
                ) : (
                  <FaLock className="me-1" />
                )}
                {isChatEnabled ? t('enabled') : t('disabled') }
              </Button>

            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleRefreshChat}
              disabled={chatLoading}
            >
              <FaSync className={chatLoading ? "spin" : ""} />
            </Button>
          </div>
        </Card.Header>
        
        <Card.Body className="p-0">
          <div className="chat-container">
            {/* حالة الشات إذا كان مغلقاً */}
            {isChatEnabled && (
              <div className="chat-disabled-overlay p-4 text-center">
                <div className="chat-disabled-content">
                  <FaLock size={48} className="text-muted mb-3" />
                  <h6 className="text-muted">{t('chat_is_disabled')}</h6>
                  <p className="text-muted small">{t('chat_disabled_description')}</p>
                </div>
              </div>
            )}

            <div 
              className="chat-messages p-3"
              style={{ 
                height: "400px", 
                overflowY: "auto",
                background: "url('https://sleekflow.io/static/images/whatsapp-button-generator/whatsApp-bg.jpeg')",
                backgroundSize: "cover"
              }}
            >
              {chatLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">{t('loading_messages')}</p>
                </div>
              ) : !Array.isArray(orderChats) || orderChats.length === 0 ? (
                <div className="text-center py-4">
                  <FaMessage size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">{t('no_messages_yet')}</h5>
                </div>
              ) : (
                <div className="messages-container">
                  {messageGroups.length === 0 ? (
                    <div className="text-center py-4">
                      <FaMessage size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">{t('no_messages_yet')}</h5>
                    </div>
                  ) : (
                    messageGroups.map(([date, dateMessages]) => (
                      <div key={date}>
                        {/* Date Separator */}
                        <div className="date-separator text-center my-3">
                          <Badge bg="light" text="dark" className="px-3 py-2">
                            {date}
                          </Badge>
                        </div>

                        {/* Messages for this date */}
                        {dateMessages.map((message) => {
                          const isCurrentUserMessage = isCurrentUser(message);
                          
                          return (
                            <div
                              key={message.id || message.created_at}
                              className={`message-bubble mb-3 ${
                                isCurrentUserMessage ? 'admin-message' :
                                message.sender?.role === 'Driver' ? 'driver-message' : 
                                message.sender?.role === 'Customer' ? 'customer-message' : 'admin-message'
                              }`}
                            >
                              <div className="message-header d-flex align-items-center mb-1">
                                <div className="d-flex align-items-center">
                                  <FaUser className="me-2 text-muted" size={12} />
                                  <strong className="me-2">{message.sender?.name || t('unknown_sender')}</strong>
                                  <Badge 
                                    bg={
                                      message.sender?.role === 'Admin' ? 'danger' : 
                                      message.sender?.role === 'Driver' ? 'info' : 
                                      message.sender?.role === 'Customer' ? 'success' : 'secondary'
                                    }
                                    className="me-2"
                                  >
                                    {t(message.sender?.role?.toLowerCase() || 'unknown')}
                                  </Badge>
                                  {transportOrder.customer_id === null && !message.is_read && !isCurrentUserMessage && (
                                    <Badge bg="warning" className="me-2" size="sm">
                                      {t('unread')}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              <div className="message-content p-2 rounded">
                                {message.has_attachments && message.attachments && message.attachments.length > 0 && (
                                  <div className="mb-2">
                                    {message.attachments.map((attachment, attIdx) => (
                                      <div key={attIdx}>
                                        {renderAttachment(attachment)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {message.message && (
                                  <div className={message.has_attachments ? "mt-2" : ""}>
                                    {message.message}
                                  </div>
                                )}
                              </div>

                              <div className="message-time text-muted small">
                                <FaClock className="mx-1" size={10} />
                                {formatMessageTime(message.created_at)}
                                {isCurrentUserMessage && !message.is_read && (
                                  <span className="ms-2 text-warning">• {t('sent')}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Section - فقط إذا كان customer_id = null والـ chat مفعل */}
            {transportOrder && transportOrder.customer_id === null && isChatEnabled && (
              <div className="chat-input-section border-top p-3 bg-white">
                {filePreview && (
                  <div className="file-preview mb-2 p-2 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <img 
                          src={filePreview} 
                          alt={t('preview')}
                          style={{ width: '40px', height: '40px', objectFit: 'cover', marginRight: '10px' }}
                        />
                        <small>{selectedFile.name}</small>
                      </div>
                      <Button variant="outline-danger" size="sm" onClick={removeSelectedFile}>
                        <FaTimes />
                      </Button>
                    </div>
                  </div>
                )}
                
                <InputGroup>
                  <Button
                    variant="outline-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                  >
                    <FaImage />
                  </Button>
                  <Form.Control
                    placeholder={t('type_message')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={sending}
                    as="textarea"
                    rows={1}
                    style={{ resize: "none" }}
                  />
                  <Button
                    variant="primary"
                    onClick={sendMessage}
                    disabled={sending || (newMessage.trim() === "" && !selectedFile)}
                  >
                    {sending ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaPaperPlane />
                    )}
                  </Button>
                </InputGroup>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  style={{ display: 'none' }}
                />
                
                <small className="text-muted d-block mt-2">
                  {t('supports_files')} (10MB max)
                </small>
              </div>
            )}

            {/* Info Box - إذا كان customer_id ≠ null أو الشات غير مفعل */}
            {(transportOrder && transportOrder.customer_id !== null || !isChatEnabled) && (
              <div className="chat-info-box bg-light border-top p-3">
                <div className="d-flex align-items-center justify-content-center">
                  {!isChatEnabled ? (
                    <>
                      <FaLock className="me-2 text-warning" />
                      <small className="text-muted">
                        {t('chat_is_disabled_by_admin')}
                      </small>
                    </>
                  ) : (
                    <>
                      <FaInfoCircle className="me-2 text-info" />
                      <small className="text-muted">
                        {t('transport_order_chat_view_only')}
                      </small>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  const toggleChatStatus = async () => {
    if (!transportOrder?.id) return;
  
    setIsTogglingChat(true);
    try {
      const response = await transportOrdersAPI.toggleChat(transportOrder.id);
      if (response.success) {
        setIsChatEnabled(response.data.is_chat_enabled);
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: response.data.is_chat_enabled ? t('chat_enabled_success') : t('chat_disabled_success'),
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        throw new Error(response.message || t('failed_to_toggle_chat'));
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: error.message || t('failed_to_toggle_chat'),
        timer: 3000,
      });
    } finally {
      setIsTogglingChat(false);
    }
  };

  // باقي الدوال كما هي ...
  const getStatusBadge = (status) => {
    const statusConfig = {
      cancelled: { variant: 'danger', text: t('cancelled') },
      rejected: { variant: 'danger', text: t('rejected') },
      assigned: { variant: 'info', text: t('assigned') },
      received: { variant: 'secondary', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'warning', text: t('pending') },
      approved: { variant: 'success', text: t('approved') }
    };
  
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMinDateTime = (baseDate = new Date()) => {
    const adjustedDate = new Date(baseDate.getTime() + 60 * 1000);
    const isoString = adjustedDate.toISOString();
    return isoString.slice(0, 16);
  };

  const handleMarkPaid = async (id) => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_paid_order'),
      icon: 'success',
      showCancelButton: true,
      confirmButtonColor: 'rgba(24, 202, 0, 1)',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('paid'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await transportOrdersAPI.markPaid(id);
        
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: t("success"),
            text: t("booking.marked_paid_successfully"),
            timer: 3000,
            showConfirmButton: false,
          });
          fetchTransportOrderDetails();
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: err.message || t("failed_to_mark_paid"),
          timer: 3000,
        });
      }
    }
  };

  const handleViewLiveTracking = (id) => {
    navigate(`/admin/transports/live-tracking/${id}`);
  };

  const handleDeleteTransportOrder = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_delete_order'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('delete'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await transportOrdersAPI.delete(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('deleted'),
            text: t('order_deleted_successfully'),
            timer: 2000,
            showConfirmButton: false
          });
          navigate('/admin/transport-orders');
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_delete_order'),
          timer: 2000,
        });
      }
    }
  };

  const handleBack = () => {
    const state = {
      fromPage: location.state?.fromPage || 1,
      currentPage: location.state?.currentPage || 1,
      highlightId: location.state?.highlightId || parseInt(id),
      scrollToHighlight: true,
      activeTab: location.state?.activeTab || 'active'
    };
    
    console.log('Navigating back with state:', state);
    
    navigate('/admin/transport-orders', { 
      state: state
    });
  };

  if (loading) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
        <span className="ms-2">{t('loading')}</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="danger" className="m-3">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!transportOrder) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="warning" className="m-3">
          {t('transport_not_found')}
          <Button variant="outline-warning" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-1 bg-light min-vh-100 booking-order-details">
      {/* Header Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <FaTruck className="text-primary me-3" size={32} />
                <div>
                  <h2 className="mb-1 fw-bold">
                    {t("booking.order_details")} - <span className="text-primary">{transportOrder.order_id}</span>
                  </h2>
                  <p className="text-muted mb-0">
                    {t("booking.booking_id")}: 
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none"
                      onClick={() => navigate(`/admin/bookings/details/${transportOrder.transport?.id}`)}
                    >
                      {transportOrder.transport?.booking_id}
                    </Button>
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <div className="d-flex flex-column align-items-end">
                <div className="mb-2">
                  {getStatusBadge(transportOrder.status)}

                  <Badge bg={transportOrder.payment_status ? 'success' : 'warning'} className="ms-2">
                    {t(transportOrder.payment) || t('unpaid')}
                  </Badge>
    
                  <Badge bg="info" className="ms-2">
                    {t(transportOrder.transport.transport_type)}
                  </Badge>

                </div>
                <small className="text-muted">
                  {t('booking.created_on')}: {transportOrder.created_at}
                </small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {(transportOrder.status === 'cancelled' || transportOrder.status === 'canceled' || transportOrder.transport?.status === 'cancelled' || transportOrder.transport?.status === 'canceled' || transportOrder.transport?.deleted_reason || transportOrder.transport?.cancelled_reason) && (
        <Alert variant="danger" className="mb-4">
          <strong>{t("cancellation_reason")}:</strong>{" "}
          {transportOrder.transport?.deleted_reason || transportOrder.transport?.cancelled_reason || t("no_cancellation_reason")}
        </Alert>
      )}

      {/* Quick Actions Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title">
          <h5 className="mb-0 fw-bold">
            <FaCog className="me-2 text-primary" />
            {t("booking.actions_section")}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-3">
              <div className="d-grid gap-2">
                {transportOrder.payment_status === 0 && (
                  <Button 
                    size="sm"
                    variant={transportOrder.payment_status ? "success" : "outline-success"}
                    onClick={() => handleMarkPaid(transportOrder.id)}
                    disabled={updating || transportOrder.payment_status}
                  >
                    <FaEuroSign /> {t("booking.mark_paid")}
                  </Button>
                )}
                {transportOrder && (transportOrder.status !== 'completed' && transportOrder.status !== 'pending') && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleViewLiveTracking(transportOrder.id)}
                    className="mx-1"
                  >
                    <FaLocationArrow /> {t("live_tracking")}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mt-3 pt-3 border-top">
            <Col className="d-flex flex-wrap gap-2 justify-content-between">
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={handleBack} size="sm">
                  <FaArrowLeft className="me-1" />
                  {t("booking.back")}
                </Button>
                
                <Button 
                  variant="outline-danger" 
                  onClick={handleDeleteTransportOrder}
                  size="sm"
                >
                  <FaTrashAlt className="me-1" />
                  {t("delete")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" fill>
        {/* Details Tab */}
        <Tab eventKey="details" title={t("booking_details")}>
          <Row>
            {/* Left Column */}
            <Col md={8}>
              {/* Route Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaRoad className="me-2 text-primary" />
                    {t("booking.route_information")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="loading-info p-3 border rounded bg-light">
                        <h6 className="fw-bold text-success mb-3">
                          <FaMapMarkerAlt className="me-2" />
                          {t("loading_location")}
                        </h6>
                          <div className="mb-3">
                            <p className="fw-bold mb-1"> {transportOrder.transport?.booking_from}</p>
                            <small className="text-muted">
                              {t("booking.loading_date")}: {transportOrder.transport?.loading_date}
                            </small>
                            <br />
                            <small className="text-muted">
                              {t("booking.loading_time")}: {transportOrder.transport?.loading_time}
                            </small>
                          </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="unloading-info p-3 border rounded bg-light">
                        <h6 className="fw-bold text-danger mb-3">
                          <FaMapMarkerAlt className="me-2" />
                          {t("unloading_location")} ({transportOrder.transport?.booking_to?.length || 0})
                        </h6>
                        <div className="mb-3">
                          <p className="fw-bold mb-1"> {transportOrder.transport?.booking_to}</p>
                          <small className="text-muted">
                            {t("booking.unloading_date")}: {transportOrder.transport?.unloading_date}
                          </small>
                            <br />
                          <small className="text-muted">
                            {t("booking.unloading_time")}: {transportOrder.transport?.unloading_time}
                          </small>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Route Summary */}
                  <Row className="mt-3">
                    <Col md={4}>
                      <div className="text-center p-2 border rounded">
                        <FaRoad className="text-info mb-2" size={24} />
                        <h6 className="mb-1">{t("booking.distance")}</h6>
                        <p className="fw-bold mb-0">{transportOrder.transport?.distance}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-2 border rounded">
                        <FaWeightHanging className="text-warning mb-2" size={24} />
                        <h6 className="mb-1">{t("booking.total_weight")}</h6>
                        <p className="fw-bold mb-0">{formatNumber(transportOrder.transport?.total_weight)} Kg</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-2 border rounded">
                        <FaBoxesStacked className="text-success mb-2" size={24} />
                        <h6 className="mb-1">{t("booking.transport_type")}</h6>
                        <p className="fw-bold mb-0">{t(transportOrder.transport?.transport_type)}</p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Driver & Vehicle Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaTruck className="me-2 text-success" />
                    {t("booking.driver_vehicle_info")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="driver-info p-3 border rounded bg-light">
                        <h6 className="fw-bold mb-3">
                          <FaUser className="me-2" />
                          {t("driver_information")}
                        </h6>
                        <p><strong>{t("driver_name")}:</strong> {transportOrder.driver_name}</p>
                        <p><strong>{t("driver_email")}:</strong> {transportOrder.driver_email}</p>
                        <p><strong>{t("phone")}:</strong> {transportOrder.phone_number}</p>
                        <p><strong>{t("driver_role")}:</strong> {transportOrder.driver_role}</p>
                        <p><strong>{t("current_location")}:</strong> {transportOrder.current_location}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="vehicle-info p-3 border rounded bg-light">
                        <h6 className="fw-bold mb-3">
                          <FaTruck className="me-2" />
                          {t("booking.vehicle_information")}
                        </h6>
                        <p><strong>{t("vehicle_type")}:</strong> {transportOrder.vehicle_type}</p>
                        <p><strong>{t("vehicle_driver_name")}:</strong> {transportOrder.vehicle_driver_name}</p>
                        <p><strong>{t("driving_choice")}:</strong> {t(transportOrder.driving_choice)}</p>
                        {transportOrder.driving_choice === "drive" && (
                          <p><strong>{t("vehicle_number")}:</strong> {transportOrder.vehicle_number}</p>
                        )}
                        <p><strong>{t("booking.price")}:</strong> {transportOrder.price} EUR</p>
                        <p><strong>{t("booking.info")}:</strong> {transportOrder.info}</p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Order Chat */}
              {renderOrderChat()}
            </Col>

            {/* Right Column */}
            <Col md={4}>
              {/* Order Timeline */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaHistory className="me-2 text-info" />
                    {t("booking.order_timeline")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-marker bg-success"></div>
                      <div className="timeline-content">
                        <h6 className="fw-bold">{t("booking.created_on")}</h6>
                        <p className="text-muted mb-0">{transportOrder.created_at}</p>
                      </div>
                    </div>
                    
                    {transportOrder.assign_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-info"></div>
                        <div className="timeline-content">
                          <h6 className="fw-bold">{t("booking.assigned_on")}</h6>
                          <p className="text-muted mb-0">{transportOrder.assign_date}</p>
                        </div>
                      </div>
                    )}
                    
                    {transportOrder.received_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-warning"></div>
                        <div className="timeline-content">
                          <h6 className="fw-bold">{t("booking.received_on")}</h6>
                          <p className="text-muted mb-0">{transportOrder.received_date}</p>
                        </div>
                      </div>
                    )}
                    
                    {transportOrder.completed_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-primary"></div>
                        <div className="timeline-content">
                          <h6 className="fw-bold">{t("booking.completed_on")}</h6>
                          <p className="text-muted mb-0">{transportOrder.completed_date}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Price Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaEuroSign className="me-2 text-success" />
                    {t("booking.price_information")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="price-section">
                    <div className=" mb-3">
                      <div className="mb-2 d-flex align-items-center gap-2">
                        <strong>{t('percentage')}:</strong>
                        <Badge bg={transportOrder.apply_commission ? "success" : "secondary"}>
                          {transportOrder.apply_commission ? "نسبة المنصة مفعّلة" : "نسبة المنصة معطّلة"}
                        </Badge>
                      </div>
                      <div className="mb-2"><strong>{t("booking.vat_amount")}:</strong> {transportOrder.vat_amount} EUR</div>
                      <div className="mb-2"><strong>{t('Gesamt Bertrag')}</strong></div>
                      <div className="mb-2"><strong>{t("exkl. 19% MwSt. Brutto")}</strong> {transportOrder.platform_percentage} EUR</div>
                    </div>

                    {transportOrder.offer_date && (
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="fw-bold">{t("booking.offer_date")}:</span>
                        <span className="fw-bold text-secondary fs-6">{transportOrder.offer_date}</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold">{t("booking.current_price")}:</span>
                      <span className="fw-bold text-success fs-5">{transportOrder.price} EUR</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Live Tracking */}
              {transportOrder.liveTracking && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header className="fw-bold card-title">
                    <h5 className="mb-0 fw-bold">
                      <FaMapMarkerAlt className="me-2 text-danger" />
                      {t("live_tracking")}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="tracking-info">
                      <p><strong>{t("current_location")}:</strong></p>
                      <p className="mb-1">Lat: {transportOrder.liveTracking.lat}</p>
                      <p className="mb-1">Lng: {transportOrder.liveTracking.lng}</p>
                      <small className="text-muted">
                        {t("last_updated")}: {transportOrder.liveTracking.recorded_at}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        {/* Logs Tab */}
        <Tab eventKey="logs" title={t("admin_logs")}>
          <Card className="shadow-sm">
            <Card.Header className="fw-bold card-title">
              <h5 className="mb-0 fw-bold">
                <FaHistory className="me-2" />
                {t("logs")}
              </h5>
            </Card.Header>
            <Card.Body>
              {transportOrder.admin_logs && transportOrder.admin_logs.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-dark">
                      <tr>
                        <th>{t("admin_name")}</th>
                        <th>{t("admin_email")}</th>
                        <th>{t("action")}</th>
                        <th>{t("description")}</th>
                        <th>{t("created_at")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transportOrder.admin_logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.admin_name || "N/A"}</td>
                          <td>{log.admin_email || "N/A"}</td>
                          <td>{log.action}</td>
                          <td>{log.description}</td>
                          <td>{log.created_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FaHistory size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">{t("no_logs_found")}</h5>
                  <p className="text-muted">{t("no_activity_logs")}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
}
